/***************
 * *******
 * *******
 * Intervene Selector
 * *******
 * *******
 *
 * Given a State, chooses an Action word to intervene on
 *
 * In this context, "State" is defined as an object which contains all features necessary
 * to choose a word to intervene on
 *
 * "Action" is a word to intervene on.
 *
 ****************/
import { interventionSituationTypes } from "../constants";
import {
  getWordMetadata,
  createSelectedWordFields,
} from "../utils/metadataUtils";
import getConfigForLocale from "../config/getConfigForLocale";
import Pass from "../intervention_selector/passes/Pass";
import getIntervention from "../interventions/interventionFactory";
import { interventionTypes } from "../constants";

// TODO consider adding the following logic here:
/*
  wordShouldIntervene
  alreadyIntervened - one word intervention on a given word per story
*/

// returns a word to intervene on, or null
export default function selectWord(state) {
  let situationType = getSituationType(state);
  switch (situationType) {
    case interventionSituationTypes.WORD:
      // If this is a word level intervention, do nothing. A word is already picked.
      break;
    case interventionSituationTypes.PHRASE:
    case interventionSituationTypes.END_OF_STORY:
      // Pick a word if there's errors
      let { chosenWord, chosenWordIndex } = getPhraseLevelSelection(state);
      state.word = chosenWord;
      state.wordIndex = chosenWordIndex;
      createSelectedWordFields(chosenWord, state);
      break;
    default:
      console.log("INTERVENE SELECTION ERROR: invalid situation type");
      break;
  }

  // set a list of invalid interventions for this run
  state.invalidInterventions = getInvalidInterventionsForRun(state);
}

// Returns a bool indicating whether we're at the end of a story
function getIsEndOfStory(state) {
  if (state.storyPhrases.length - 1 == state.phraseIndex) {
    // Don't throw phrase intervention at end of story
    if (state.endOfPhrase) {
      return true;
    }
    // Don't throw word intervention on last word of story
    if (state.phraseLength - 1 == state.wordIndex) {
      return true;
    }
  }
  return false;
}

// Returns an enum representing the type of intervention situation
function getSituationType(state) {
  // Check if we're at the end of a story
  let atEndOfStory = getIsEndOfStory(state);
  if (atEndOfStory) {
    return interventionSituationTypes.END_OF_STORY;
  }

  if (!state.endOfPhrase) {
    return interventionSituationTypes.WORD;
  }
  return interventionSituationTypes.PHRASE;
}

// Based on selected word, search through intervention history, and disqualify used intervention/word pairs for this run
function getInvalidInterventionsForRun(state) {
  let invalidInterventions = [];
  state.interventionHistoryByPhrase.forEach((phraseInterventionHistory) => {
    if (phraseInterventionHistory) {
      phraseInterventionHistory.forEach((intervention) => {
        if (
          intervention.word &&
          state.word &&
          intervention.word.toLowerCase() == state.word.toLowerCase()
        ) {
          invalidInterventions.push(intervention.type);
        }
      });
    }
  });
  return invalidInterventions;
}

// TODO calculate a miscue value that incorporates studentAREA into the decision under certain conditions
function getMiscueInterventionValue(miscue) {
  console.log(miscue);
  //let predConf = miscue.
}

function getPhraseLevelSelection(state) {
  // First, filter out any words that we already intervened on this phrase
  let errors = JSON.parse(JSON.stringify(state.errorsInPhrase));
  let phraseInterventionHistory =
    state.interventionHistoryByPhrase[state.phraseIndex];
  if (phraseInterventionHistory) {
    errors = errors.filter((error) => {
      return !phraseInterventionHistory.some((intervention) => {
        return (
          intervention.word &&
          intervention.word.toLowerCase() == error.word.toLowerCase()
        );
      });
    });
  }

  // Then, calculate selection value for each word, order by predConf, and select first word that has metadata to support intervention in phraseInterventionWeights
  if (errors.length > 0) {
    const predConfOrderedArray = [];

    errors.forEach((error) => {
      const errorMetadata = getWordMetadata(error.word, state);

      if (
        state &&
        state.confData &&
        state.confData[error.index] &&
        errorMetadata
      ) {
        errorMetadata.predConf =
          state.confData[error.index].correct_confidences;
      }

      if (errorMetadata && errorMetadata.predConf) {
        predConfOrderedArray.push({
          word: error.word,
          index: error.index,
          metadata: errorMetadata,
        });
      }
    });

    if (predConfOrderedArray.length > 0) {
      // Sort so lowest predConf values come first.
      predConfOrderedArray.sort((a, b) => {
        return a.metadata.predConf - b.metadata.predConf;
      });

      // Loop through predConfOrderedArray, and select the first word that has metadata that supports interventions in state.phraseInterventionWeights
      for (let i = 0; i < predConfOrderedArray.length; i++) {
        const word = predConfOrderedArray[i].word;
        const wordIndex = predConfOrderedArray[i].index;

        // Create test state and test pass to see if we have valid interventions from phraseInterventionWeights
        const testState = JSON.parse(JSON.stringify(state));
        testState.word = word;
        testState.wordIndex = wordIndex;
        createSelectedWordFields(word, testState);
        const passConfigs = getConfigForLocale(state.locale);
        const testPass = new Pass(passConfigs);

        // Load all interventions into the testPass
        Object.values(interventionTypes).forEach((interventionType) => {
          testPass.addIntervention(getIntervention(interventionType));
        });

        // Qualify interventions based on testState
        testPass.qualifyInterventions(testState);

        const interventionWeights =
          testState.phraseInterventionWeights[wordIndex];
        const possibleInterventions = interventionWeights
          ? Object.keys(interventionWeights)
          : undefined;

        // If any interventions from phraseInterventionWeights are valid in the testPass, return this word
        if (possibleInterventions) {
          for (let j = 0; j < possibleInterventions.length; j++) {
            if (
              testPass
                .getValidInterventions()
                .includes(possibleInterventions[j])
            ) {
              return { chosenWord: word, chosenWordIndex: wordIndex };
            }
          }
        }
      }

      return {
        chosenWord: predConfOrderedArray[0].word,
        chosenWordIndex: predConfOrderedArray[0].index,
      };
    }

    return { chosenWord: null, chosenWordIndex: null };
  } else {
    return { chosenWord: null, chosenWordIndex: null };
  }
}
